import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Pagination from "../../components/Pagination";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../styles/trainingQuiz.css"; // ✅ Add this line

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const safeParse = (str, fallback = []) => {
  try {
    if (!str) return fallback;
    if (typeof str === "object") return Array.isArray(str) ? str : fallback;
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export default function TrainingQuiz() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [currentPageModules, setCurrentPageModules] = useState(1);
  const [currentPageQuestions, setCurrentPageQuestions] = useState(1);
  const [programPageMap, setProgramPageMap] = useState({});
  const itemsPerPage = 10;

  const [searchModuleQuery, setSearchModuleQuery] = useState("");

  const [modules, setModules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [progName, setProgName] = useState("");
  const [progCats, setProgCats] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [moduleForm, setModuleForm] = useState({
    tm_key: "",
    tm_title: "",
    tm_type: "quiz",
    tm_deadline: "",
    tm_video_url: "",
    tm_pdf_url: "",
    tm_category: "Learning Centre",
    tm_lang: "ENG",
  });

  const getPaginatedData = (data, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const loadModules = useCallback(async () => {
    try {
      setErr("");
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/trainings/modules/all`, {
        headers: authHeader(),
      });
      setModules(res.data || []);
    } catch (e) {
      console.error(e);
      setErr("Failed to load modules");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  const loadPrograms = useCallback(async () => {
    try {
      const [progRes, catRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/trainings/programs/all`, {
          headers: authHeader(),
        }),
        axios.get(`${BASE_URL}/api/dropdowns/categories`, {
          headers: authHeader(),
        }),
      ]);
      setPrograms(progRes.data || []);
      setCategories(
        (catRes.data || []).map((c) => ({ label: c.c_name, value: c.c_id }))
      );
    } catch (e) {
      console.error("Failed to load programs:", e);
    }
  }, [BASE_URL]);

  useEffect(() => {
    loadModules();
    loadPrograms();
  }, [loadModules, loadPrograms]);

  useEffect(() => {
    if (editingModule) setCurrentPageQuestions(1);
  }, [editingModule]);

  useEffect(() => {
    setProgramPageMap({});
  }, [programs]);

  const saveProgram = async (p) => {
    try {
      const payload = {
        name: p.tp_name,
        moduleIds: safeParse(p.tp_module_ids, []),
        category: safeParse(p.tp_category, []),
        active: p.tp_active || 1,
      };

      await axios.put(
        `${BASE_URL}/api/trainings/programs/${p.tp_id}`,
        payload,
        {
          headers: authHeader(),
        }
      );

      await loadPrograms();

      // ✅ Show success message
      alert("✅ Program saved successfully!");
    } catch (e) {
      console.error("Save program failed:", e);
      alert("❌ Failed to save program. Please try again.");
    }
  };

  const createProgram = async () => {
    const payload = {
      name: progName,
      moduleIds: [],
      category: progCats.map((c) => c.value),
    };
    await axios.post(`${BASE_URL}/api/trainings/programs`, payload, {
      headers: authHeader(),
    });
    setShowProgramModal(false);
    setProgName("");
    setProgCats([]);
    await loadPrograms();
  };

  const deleteProgram = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    await axios.delete(`${BASE_URL}/api/trainings/programs/${id}`, {
      headers: authHeader(),
    });
    await loadPrograms();
  };

  const loadQuestions = async (moduleId) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/trainings/modules/${moduleId}/questions`,
        { headers: authHeader() }
      );
      setQuestions(res.data || []);
    } catch (e) {
      console.error(e);
      setQuestions([]);
    }
  };

  // -------- MODULE ACTIONS --------
  const openNewModule = () => {
    setEditingModule(null);
    setModuleForm({
      tm_key: "",
      tm_title: "",
      tm_type: "quiz",
      tm_deadline: "",
      tm_video_url: "",
      tm_pdf_url: "",
      tm_category: "Learning Centre",
      tm_lang: "ENG",
    });

    // ✅ Reset program selection when starting new module
    setSelectedPrograms([]);

    setShowModuleModal(true);
  };

  const openEditModule = async (m) => {
    setEditingModule(m);
    setModuleForm(m);

    // ✅ Preload assigned programs into the dropdown
    const assignedIds = Array.isArray(m.tm_program_ids)
      ? m.tm_program_ids
      : typeof m.tm_program_ids === "string"
      ? JSON.parse(m.tm_program_ids || "[]")
      : [];

    setSelectedPrograms(
      programs
        .filter((p) => assignedIds.includes(p.tp_id))
        .map((p) => ({ label: p.tp_name, value: p.tp_id }))
    );

    await loadQuestions(m.tm_id);
    setShowModuleModal(true);
  };

  const saveModule = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("⚠️ You are not logged in. Please log in again.");
      return;
    }

    try {
      setIsUploading(true); // ✅ start overlay

      // Start listening for compression progress
      const evtSource = new EventSource(
        `${BASE_URL}/api/trainings/compression-progress`
      );
      evtSource.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.percent) setUploadProgress(data.percent);
          if (data.done) {
            setUploadProgress(100);
            setTimeout(() => evtSource.close(), 1500);
          }
        } catch (err) {
          console.error("SSE parse error:", err);
        }
      };

      // ✅ Always have a key
      let key = moduleForm.tm_key?.trim();
      if (!key) {
        key = moduleForm.tm_title
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
          .replace(/^\w/, (c) => c.toLowerCase());
      }

      let videoUrl = moduleForm.tm_video_url || "";
      let pdfUrl = moduleForm.tm_pdf_url || "";

      // ✅ Upload video
      if (moduleForm.selectedVideo) {
        const formData = new FormData();
        formData.append("tm_key", key);
        formData.append("video", moduleForm.selectedVideo);

        const res = await axios.post(
          `${BASE_URL}/api/trainings/upload`,
          formData,
          {
            headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
          }
        );

        if (res.data?.videoUrl) videoUrl = res.data.videoUrl;
        else throw new Error("Video upload failed");
      }

      // ✅ Upload PDF
      if (moduleForm.selectedPDF) {
        const formData = new FormData();
        formData.append("tm_key", key);
        formData.append("pdf", moduleForm.selectedPDF);

        const res = await axios.post(
          `${BASE_URL}/api/trainings/upload`,
          formData,
          {
            headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
          }
        );

        if (res.data?.pdfUrl) pdfUrl = res.data.pdfUrl;
        else throw new Error("PDF upload failed");
      }

      // ✅ Prepare payload
      const payload = {
        ...moduleForm,
        tm_key: key,
        tm_video_url: videoUrl,
        tm_pdf_url: pdfUrl,
        tm_deadline:
          !moduleForm.tm_deadline || moduleForm.tm_deadline.trim() === ""
            ? null
            : moduleForm.tm_deadline,
        tm_program_ids: selectedPrograms.map((p) => p.value),
      };

      // ✅ Save (create or update)
      let result;
      if (editingModule) {
        result = await axios.put(
          `${BASE_URL}/api/trainings/modules/${editingModule.tm_id}`,
          payload,
          { headers: authHeader() }
        );
      } else {
        result = await axios.post(
          `${BASE_URL}/api/trainings/modules`,
          payload,
          {
            headers: authHeader(),
          }
        );
      }

      if (result.data?.message?.toLowerCase().includes("success")) {
        alert("✅ Module saved successfully!");
        setShowModuleModal(false);
        await Promise.all([loadModules(), loadPrograms()]);
        setSelectedPrograms([]);
      } else {
        throw new Error("Module creation did not return success message");
      }
    } catch (err) {
      console.error("❌ Module save failed:", err);
      alert(
        "❌ Failed to save module. " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteModule = async (id) => {
    if (!window.confirm("Delete this module and all its questions?")) return;
    await axios.delete(`${BASE_URL}/api/trainings/modules/${id}`, {
      headers: authHeader(),
    });
    await loadModules();
  };

  // -------- QUESTION ACTIONS --------
  const openNewQuestion = () => {
    setEditingQuestion(null);
    setShowQuestionModal(true);
  };

  const openEditQuestion = (q) => {
    setEditingQuestion(q);
    setShowQuestionModal(true);
  };

  const saveQuestion = async (qObj) => {
    try {
      if (editingQuestion) {
        await axios.put(
          `${BASE_URL}/api/trainings/questions/${editingQuestion.tq_id}`,
          qObj,
          { headers: authHeader() }
        );
      } else {
        await axios.post(
          `${BASE_URL}/api/trainings/modules/${editingModule.tm_id}/questions`,
          qObj,
          { headers: authHeader() }
        );
      }
      await loadQuestions(editingModule.tm_id);
      setShowQuestionModal(false);
    } catch (e) {
      alert("Failed to save question");
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await axios.delete(`${BASE_URL}/api/trainings/questions/${id}`, {
      headers: authHeader(),
    });
    await loadQuestions(editingModule.tm_id);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {isUploading && (
        <div className="upload-overlay">
          <div className="spinner-border" role="status"></div>
          <div className="upload-overlay-text">
            ⏳ Updating module...
            {uploadProgress > 0 && (
              <>
                <br />
                <small>Compressing video</small>
              </>
            )}
          </div>

          {uploadProgress > 0 && (
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{ width: `${Number(uploadProgress)}%` }}
              ></div>
              <span>{Number(uploadProgress || 0).toFixed(1)}%</span>
            </div>
          )}
        </div>
      )}

      <div className="form-wrapper my-2">
        <h2>Training Quiz</h2>
        {err && <div className="alert alert-danger">{err}</div>}

        <fieldset className="border p-3 my-3">
          <legend>Modules</legend>

          {/* Add Module Button (Top Row) */}
          <div className="mb-2">
            <button className="btn btn-primary" onClick={openNewModule}>
              + Add Module
            </button>
          </div>

          {/* Search Bar (Below in Separate Row) */}
          <div className="input-group mb-3">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search modules..."
              value={searchModuleQuery}
              onChange={(e) => {
                setSearchModuleQuery(e.target.value);
                setCurrentPageModules(1); // reset to first page on search
              }}
            />
          </div>

          <div className="list-group">
            <div className="mb-2">
              {getPaginatedData(
                modules.filter((m) =>
                  m.tm_title
                    .toLowerCase()
                    .includes(searchModuleQuery.toLowerCase())
                ),
                currentPageModules
              ).map((m) => (
                <div
                  key={m.tm_id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>{m.tm_title}</span>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => openEditModule(m)}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteModule(m.tm_id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPageModules}
              totalItems={
                modules.filter((m) =>
                  m.tm_title
                    .toLowerCase()
                    .includes(searchModuleQuery.toLowerCase())
                ).length
              }
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPageModules}
            />
          </div>
        </fieldset>

        {/* PROGRAMS BY CATEGORY */}
        <fieldset className="border p-3">
          <legend>Programs by Category</legend>
          <button
            className="btn btn-outline-primary mb-2"
            onClick={() => setShowProgramModal(true)}
          >
            + Add Program
          </button>

          {programs.map((p) => (
            <div key={p.tp_id} className="card shadow-sm mb-3">
              <div className="card-body">
                <h5 className="fw-bold mb-2">{p.tp_name}</h5>

                {/* Category Tags */}
                <div className="mb-3">
                  <Select
                    isMulti
                    options={categories}
                    placeholder="Select Categories..."
                    value={safeParse(p.tp_category, []).map((id) =>
                      categories.find((c) => c.value === id)
                    )}
                    onChange={(opts) => {
                      const ids = (opts || []).map((o) => o.value);
                      p.tp_category = JSON.stringify(ids);
                      setPrograms([...programs]);
                    }}
                  />
                </div>

                {/* --- Add Training Dropdown (same style as Interview Manager) --- */}
                <Select
                  options={modules
                    .filter(
                      (m) => !safeParse(p.tp_module_ids, []).includes(m.tm_id)
                    )
                    .map((m) => ({ label: m.tm_title, value: m.tm_id }))}
                  onChange={(opt) => {
                    if (!opt) return;
                    const ids = safeParse(p.tp_module_ids, []);
                    if (!ids.includes(opt.value)) {
                      p.tp_module_ids = JSON.stringify([...ids, opt.value]);
                      setPrograms([...programs]);
                    }
                  }}
                  placeholder="Add Training..."
                />

                {/* Display Modules with Working Pagination */}
                <div className="list-group mt-2">
                  {getPaginatedData(
                    safeParse(p.tp_module_ids, []),
                    programPageMap[p.tp_id] || 1
                  ).map((id) => {
                    const mod = modules.find((m) => m.tm_id === id);
                    if (!mod) return null;

                    return (
                      <div
                        key={id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {mod.tm_title}
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            const ids = safeParse(p.tp_module_ids, []).filter(
                              (mid) => mid !== id
                            );
                            p.tp_module_ids = JSON.stringify(ids);
                            setPrograms([...programs]);
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Save / Delete Buttons */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <button
                    className="btn btn-success"
                    onClick={() => saveProgram(p)}
                  >
                    Save
                  </button>

                  {/* Centered Pagination */}
                  <div className="flex-grow-1 d-flex justify-content-center custom-pagination">
                    <Pagination
                      currentPage={programPageMap[p.tp_id] || 1}
                      totalItems={safeParse(p.tp_module_ids, []).length}
                      itemsPerPage={itemsPerPage}
                      onPageChange={(page) =>
                        setProgramPageMap((prev) => ({
                          ...prev,
                          [p.tp_id]: page,
                        }))
                      }
                    />
                  </div>

                  <button
                    className="btn btn-danger"
                    onClick={() => deleteProgram(p.tp_id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </fieldset>

        {/* MODULE MODAL */}
        <Modal
          show={showModuleModal}
          onHide={() => {
            setShowModuleModal(false);
            setSelectedPrograms([]); // ✅ clear selection
          }}
          size="xl"
          dialogClassName="training-module-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingModule ? "Edit Module" : "New Module"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* --- Title and Auto Key Generation --- */}
            <div className="mb-3">
              <label className="form-label">Title</label>
              <input
                className="form-control"
                value={moduleForm.tm_title}
                onChange={(e) => {
                  const title = e.target.value;
                  const key = title
                    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
                    .replace(/^\w/, (c) => c.toLowerCase());
                  setModuleForm({
                    ...moduleForm,
                    tm_title: title,
                    tm_key: key,
                  });
                }}
              />
              {/* <small className="text-muted">Auto Key: {moduleForm.tm_key}</small> */}
            </div>

            {/* --- Category --- */}
            <div className="mb-3">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={moduleForm.tm_category}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, tm_category: e.target.value })
                }
              >
                <option value="">-- Select a Category --</option>
                <option value="Learning Centre">Learning Centre</option>
              </select>
            </div>

            {/* --- Language --- */}
            <div className="mb-3">
              <label className="form-label">Language</label>
              <select
                className="form-select"
                value={moduleForm.tm_lang}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, tm_lang: e.target.value })
                }
              >
                <option value="">-- Select Language --</option>
                <option value="ENG">ENG</option>
              </select>
            </div>

            {/* --- Select Training Video --- */}
            <div className="mb-3">
              <label className="form-label">Select Video file:</label>
              <input
                type="file"
                className="form-control"
                accept="video/mp4"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  const maxSizeMB = 300;
                  const fileSizeMB = file.size / (1024 * 1024);

                  if (fileSizeMB > maxSizeMB) {
                    alert(
                      `⚠️ File too large (${fileSizeMB.toFixed(
                        1
                      )} MB). Please compress below ${maxSizeMB} MB before uploading.`
                    );
                    e.target.value = ""; // reset input
                    return;
                  }

                  setModuleForm({
                    ...moduleForm,
                    selectedVideo: file,
                    tm_video_url: "", // clear old filename
                  });
                }}
              />

              {/* Show selected video (new) or existing (saved) */}
              {(moduleForm.selectedVideo || moduleForm.tm_video_url) && (
                <div className="mt-2 d-flex align-items-center gap-2 uploaded-file">
                  <strong>Video:</strong>{" "}
                  {moduleForm.selectedVideo ? (
                    <span className="text-primary fw-semibold">
                      {moduleForm.selectedVideo.name}
                    </span>
                  ) : (
                    <a
                      href={`/training-files/${moduleForm.tm_video_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary fw-semibold text-decoration-none"
                    >
                      {moduleForm.tm_video_url}
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger rounded-circle ms-2"
                    title="Remove video"
                    onClick={() =>
                      setModuleForm({
                        ...moduleForm,
                        selectedVideo: null,
                        tm_video_url: "",
                      })
                    }
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              )}
            </div>

            {/* --- Select Training PDF --- */}
            <div className="mb-3">
              <label className="form-label">Select PDF file:</label>
              <input
                type="file"
                className="form-control"
                accept="application/pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setModuleForm({
                    ...moduleForm,
                    selectedPDF: file,
                    tm_pdf_url: "",
                  });
                }}
              />

              {/* Show selected PDF (new) or existing (saved) */}
              {(moduleForm.selectedPDF || moduleForm.tm_pdf_url) && (
                <div className="mt-2 d-flex align-items-center gap-2 uploaded-file">
                  <strong>PDF:</strong>{" "}
                  {moduleForm.selectedPDF ? (
                    <span className="text-success fw-semibold">
                      {moduleForm.selectedPDF.name}
                    </span>
                  ) : (
                    <a
                      href={`/training-files/${moduleForm.tm_pdf_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-success fw-semibold text-decoration-none"
                    >
                      {moduleForm.tm_pdf_url}
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger rounded-circle ms-2"
                    title="Remove PDF"
                    onClick={() =>
                      setModuleForm({
                        ...moduleForm,
                        selectedPDF: null,
                        tm_pdf_url: "",
                      })
                    }
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              )}
            </div>

            {/* --- Assign to Program --- */}
            <div className="mb-3">
              <label className="form-label">Assign to Program</label>
              <Select
                isMulti
                options={programs.map((p) => ({
                  label: p.tp_name,
                  value: p.tp_id,
                }))}
                value={selectedPrograms}
                onChange={(opts) => setSelectedPrograms(opts || [])}
              />
            </div>

            {/* --- Assign to Users --- */}
            {editingModule && (
              <div className="mb-3 d-flex gap-2">
                <Button
                  variant="outline-success"
                  onClick={async () => {
                    if (selectedPrograms.length === 0)
                      return alert("Please select at least one program first.");

                    // ✅ Confirmation dialog
                    const confirmAssign = window.confirm(
                      "Are you sure you want to assign this module to all active users in the selected programs? This may affect many users."
                    );
                    if (!confirmAssign) return;

                    try {
                      const ids = selectedPrograms.map((p) => p.value);
                      const res = await axios.post(
                        `${BASE_URL}/api/trainings/modules/${editingModule.tm_id}/assign-programs`,
                        { programIds: ids },
                        { headers: authHeader() }
                      );
                      alert(res.data.message || "✅ Assigned successfully!");
                    } catch (e) {
                      console.error(e);
                      alert("❌ Failed to assign module to users.");
                    }
                  }}
                >
                  Assign to Current Program Users
                </Button>
              </div>
            )}

            {/* --- Questions List --- */}
            {editingModule && (
              <>
                <hr />
                <h5>Questions</h5>
                <button
                  className="btn btn-outline-primary mb-2"
                  onClick={openNewQuestion}
                >
                  + Add Question
                </button>

                <div className="training-questions-container">
                  {getPaginatedData(questions, currentPageQuestions).map(
                    (q) => (
                      <div key={q.tq_id} className="training-question-row">
                        <div className="text">{q.tq_question}</div>
                        <div className="actions">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit question"
                            onClick={() => openEditQuestion(q)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete question"
                            onClick={() => deleteQuestion(q.tq_id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    )
                  )}

                  <Pagination
                    currentPage={currentPageQuestions}
                    totalItems={questions.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPageQuestions}
                  />
                </div>
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowModuleModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={saveModule}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showProgramModal}
          onHide={() => setShowProgramModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>New Program</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <label className="form-label">Program Name</label>
            <input
              className="form-control mb-2"
              value={progName}
              onChange={(e) => setProgName(e.target.value)}
            />
            <label className="form-label">Categories</label>
            <Select
              isMulti
              options={categories}
              value={progCats}
              onChange={(opts) => setProgCats(opts || [])}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowProgramModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={createProgram}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* QUESTION MODAL */}
        <QuestionEditor
          show={showQuestionModal}
          onClose={() => setShowQuestionModal(false)}
          onSave={saveQuestion}
          editingQ={editingQuestion}
        />
      </div>
    </>
  );
}

// ---- QUESTION EDITOR ----
function QuestionEditor({ show, onClose, onSave, editingQ }) {
  const [text, setText] = useState(editingQ?.tq_question || "");
  const [options, setOptions] = useState(
    editingQ?.tq_options ? safeParse(editingQ.tq_options, []) : []
  );
  const [correct, setCorrect] = useState(
    editingQ?.tq_correct ? safeParse(editingQ.tq_correct, []) : []
  );
  const [multi, setMulti] = useState(editingQ?.tq_multi ? true : false);

  useEffect(() => {
    if (editingQ) {
      setText(editingQ.tq_question);
      setOptions(safeParse(editingQ.tq_options, []));
      setCorrect(safeParse(editingQ.tq_correct, []));
      setMulti(!!editingQ.tq_multi);
    } else {
      setText("");
      setOptions([]);
      setCorrect([]);
      setMulti(false);
    }
  }, [editingQ, show]);

  const addOption = () => setOptions([...options, ""]);
  const updateOption = (i, val) => {
    const copy = [...options];
    copy[i] = val;
    setOptions(copy);
  };
  const deleteOption = (i) => setOptions(options.filter((_, idx) => idx !== i));

  const toggleCorrect = (i) => {
    const label = String.fromCharCode(65 + i);
    if (multi) {
      if (correct.includes(label))
        setCorrect(correct.filter((c) => c !== label));
      else setCorrect([...correct, label]);
    } else {
      setCorrect([label]);
    }
  };

  const handleSave = () => {
    const qObj = {
      tq_question: text,
      tq_options: options,
      tq_correct: correct,
      tq_multi: multi ? 1 : 0,
      tq_order: 0,
    };
    onSave(qObj);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{editingQ ? "Edit Question" : "New Question"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">Question</label>
          <textarea
            className="form-control"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Options</label>
          <div className="list-group">
            {options.map((opt, i) => (
              <div
                key={i}
                className="list-group-item d-flex align-items-center gap-2"
              >
                <input
                  type="text"
                  className="form-control"
                  value={opt}
                  onChange={(e) => updateOption(i, e.target.value)}
                />
                <input
                  type="checkbox"
                  checked={correct.includes(String.fromCharCode(65 + i))}
                  onChange={() => toggleCorrect(i)}
                />
                <label className="ms-1">
                  Correct ({String.fromCharCode(65 + i)})
                </label>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteOption(i)}
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
          <button
            className="btn btn-outline-secondary btn-sm mt-2"
            onClick={addOption}
          >
            + Add Option
          </button>
        </div>

        <div className="form-check mb-2">
          <input
            type="checkbox"
            className="form-check-input"
            checked={multi}
            onChange={(e) => setMulti(e.target.checked)}
          />
          <label className="form-check-label">
            Allow multiple correct answers
          </label>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
